import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const pollStatus = useCallback(
    async (attempt = 0) => {
      if (!sessionId || attempt >= 5) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setStatus(res.data);
        if (res.data.payment_status === "paid" || res.data.status === "expired") {
          setLoading(false);
          return;
        }
        setTimeout(() => pollStatus(attempt + 1), 2000);
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    },
    [sessionId]
  );

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    pollStatus();
  }, [sessionId, pollStatus]);

  const isPaid = status?.payment_status === "paid";
  const isExpired = status?.status === "expired";

  return (
    <div
      className="pt-28 lg:pt-44 min-h-screen flex items-center justify-center"
      data-testid="payment-success-page"
    >
      <div className="max-w-lg mx-auto px-6 text-center">
        {loading ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Loader2 className="mx-auto text-sage mb-6 animate-spin" size={48} />
            <h1 className="font-display text-2xl mb-2">Checking your payment...</h1>
            <p className="text-charcoal/60">This won't take long.</p>
          </motion.div>
        ) : isPaid ? (
          <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
            <CheckCircle className="mx-auto text-sage mb-6" size={64} />
            <h1 className="font-display text-3xl mb-3" data-testid="payment-confirmed">
              You're in!
            </h1>
            <p className="text-charcoal/60 mb-2">
              Your ticket for{" "}
              <strong>{status?.metadata?.event_title}</strong> has been confirmed.
            </p>
            <p className="text-charcoal/40 text-sm mb-8">
              Amount: &pound;{(status?.amount_total / 100).toFixed(2)}{" "}
              {status?.currency?.toUpperCase()}
            </p>
            <Link
              to="/community"
              className="inline-block bg-ocean text-white hover:bg-charcoal rounded-full px-8 py-3.5 font-medium transition-all duration-300"
              data-testid="back-to-events-btn"
            >
              Back to Events
            </Link>
          </motion.div>
        ) : isExpired ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <XCircle className="mx-auto text-red-400 mb-6" size={64} />
            <h1 className="font-display text-3xl mb-3">Session Expired</h1>
            <p className="text-charcoal/60 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Link
              to="/community"
              className="inline-block bg-ocean text-white rounded-full px-8 py-3.5 font-medium"
              data-testid="try-again-btn"
            >
              Back to Events
            </Link>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <XCircle className="mx-auto text-amber mb-6" size={64} />
            <h1 className="font-display text-3xl mb-3">Payment Status Unknown</h1>
            <p className="text-charcoal/60 mb-8">
              We couldn't confirm your payment. Please contact us if you've been charged.
            </p>
            <Link
              to="/community"
              className="inline-block bg-ocean text-white rounded-full px-8 py-3.5 font-medium"
              data-testid="payment-unknown-btn"
            >
              Back to Events
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
}
